import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { userService } from '../../services/userService';
import { UserFormModal } from '../../components/UserFormModal';
import { DeptFormModal } from '../../components/DeptFormModal';

type Tab = 'users' | 'roles' | 'departments';

const UserManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('users');
  const [users, setUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);

  const [userModal, setUserModal] = useState<{ mode: 'create' | 'edit'; data: any } | null>(null);
  const [deptModal, setDeptModal] = useState<{ mode: 'create' | 'edit'; data: any } | null>(null);

  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    if (currentUser?.role !== 'super_admin') { navigate('/'); return; }
    fetchData();
  }, [currentUser, navigate]);

  const fetchData = async () => {
    try {
      const [u, r, d] = await Promise.all([
        userService.getUsers(),
        userService.getRoles(),
        userService.getDepartments(),
      ]);
      setUsers(u);
      setRoles(r);
      setDepartments(d);
    } catch (err) {
      console.error('Failed to fetch data', err);
    }
  };

  const handleSaveUser = async (payload: Record<string, any>) => {
    if (userModal!.mode === 'create') {
      await userService.createUser(payload);
    } else {
      await userService.updateUser(userModal!.data.id, payload);
    }
    setUserModal(null);
    fetchData();
  };

  const handleDeactivateUser = async (user: any) => {
    if (!window.confirm(`Deactivate ${user.user_name}?`)) return;
    try {
      await userService.deactivateUser(user.id);
      fetchData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleResetUserPassword = async (user: any) => {
    if (!window.confirm(`Send password reset email to ${user.first_name} ${user.last_name} (${user.email})?`)) return;
    try {
      await userService.triggerUserPasswordReset(user.id);
      alert(`Password reset link successfully sent to ${user.email}`);
    } catch (err: any) {
      alert(err.message || 'Failed to trigger password reset');
    }
  };


  const handleSaveDept = async (payload: Record<string, any>) => {
    if (deptModal!.mode === 'create') {
      await userService.createDepartment(payload);
    } else {
      await userService.updateDepartment(deptModal!.data.id, payload);
    }
    setDeptModal(null);
    fetchData();
  };

  const handleDeactivateDept = async (dept: any) => {
    if (!window.confirm(`Deactivate department "${dept.name}"?`)) return;
    try {
      await userService.deactivateDepartment(dept.id);
      fetchData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  if (currentUser?.role !== 'super_admin') return null;

  return (
    <div className="page-container">
      <div className="page-header" style={{ marginBottom: '1.5rem' }}>
        <div className="header-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button onClick={() => navigate(-1)} style={{ color: 'var(--text-dark)', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h1 className="page-title" style={{ margin: 0 }}>{currentUser?.platform?.toUpperCase()} User Management</h1>
          </div>
          <div className="action-cell">
            <button onClick={() => navigate('/profile')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--primary)', display: 'flex', alignItems: 'center' }} title="Profile">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="32" height="32">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
        </div>
        <p className="page-description" style={{ margin: 0, marginTop: '0.5rem', paddingLeft: '2.5rem' }}>Manage users, roles, and departments (Super Admin only)</p>
      </div>

      <div className="tab-nav">
        {(['users', 'roles', 'departments'] as Tab[]).map(tab => (
          <button
            key={tab}
            className={`tab-btn${activeTab === tab ? ' active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* ── Users ── */}
      {activeTab === 'users' && (
        <>
          <div className="actions-header">
            <button className="primary-btn" onClick={() => setUserModal({ mode: 'create', data: null })}>
              + Create User
            </button>
          </div>
          <div className="list-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>No.</th><th>Username</th><th>Name</th><th>Email</th>
                  <th>Role</th><th>Department</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, idx) => (
                  <tr 
                    key={user.id} 
                    onClick={() => {
                      if (currentUser?.platform === 'dms') {
                        navigate(`/admin/users/${user.id}/documents`, { state: { user } });
                      } else {
                        navigate(`/admin/users/${user.id}/vendors`, { state: { user } });
                      }
                    }}
                    className="clickable-row" 
                    style={{ cursor: 'pointer' }}
                  >
                    <td><strong>{idx + 1}</strong></td>
                    <td>{user.user_name}</td>
                    <td>{user.first_name} {user.last_name}</td>
                    <td>{user.email}</td>
                    <td><span className="status-badge status-active">{user.role || 'None'}</span></td>
                    <td>{user.department || '—'}</td>
                    <td>
                      <span className={`status-badge ${user.status === 'active' ? 'status-active' : 'status-inactive'}`}>
                        {user.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-cell">
                        <button
                          className="secondary-btn sm-btn"
                          onClick={(e) => { e.stopPropagation(); setUserModal({ mode: 'edit', data: user }); }}
                        >
                          Edit
                        </button>
                        {user.status === 'active' && (
                          <button
                            className="secondary-btn sm-btn"
                            style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}
                            onClick={(e) => { e.stopPropagation(); handleResetUserPassword(user); }}
                          >
                            Reset PW
                          </button>
                        )}
                        {user.status === 'active' && (
                          <button
                            className="danger-btn sm-btn"
                            onClick={(e) => { e.stopPropagation(); handleDeactivateUser(user); }}
                          >
                            Deactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Roles (read-only) ── */}
      {activeTab === 'roles' && (
        <div className="list-container">
          <table className="data-table">
            <thead>
              <tr><th>ID</th><th>Name</th><th>Description</th><th>Status</th></tr>
            </thead>
            <tbody>
              {roles.map(role => (
                <tr key={role.id}>
                  <td><strong>{role.id}</strong></td>
                  <td>{role.name}</td>
                  <td>{role.description || '—'}</td>
                  <td>
                    <span className={`status-badge ${role.is_active ? 'status-active' : 'status-inactive'}`}>
                      {role.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Departments ── */}
      {activeTab === 'departments' && (
        <>
          <div className="actions-header">
            <button className="primary-btn" onClick={() => setDeptModal({ mode: 'create', data: null })}>
              + Create Department
            </button>
          </div>
          <div className="list-container">
            <table className="data-table">
              <thead>
                <tr><th>ID</th><th>Name</th><th>Description</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {departments.map(dept => (
                  <tr key={dept.id}>
                    <td><strong>{dept.id}</strong></td>
                    <td>{dept.name}</td>
                    <td>{dept.description || '—'}</td>
                    <td>
                      <span className={`status-badge ${dept.is_active ? 'status-active' : 'status-inactive'}`}>
                        {dept.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-cell">
                        <button
                          className="secondary-btn sm-btn"
                          onClick={() => setDeptModal({ mode: 'edit', data: dept })}
                        >
                          Edit
                        </button>
                        {dept.is_active && (
                          <button
                            className="danger-btn sm-btn"
                            onClick={() => handleDeactivateDept(dept)}
                          >
                            Deactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <UserFormModal
        isOpen={userModal !== null}
        mode={userModal?.mode ?? 'create'}
        initialData={userModal?.data}
        roles={roles}
        departments={departments}
        onClose={() => setUserModal(null)}
        onSave={handleSaveUser}
      />

      <DeptFormModal
        isOpen={deptModal !== null}
        mode={deptModal?.mode ?? 'create'}
        initialData={deptModal?.data}
        onClose={() => setDeptModal(null)}
        onSave={handleSaveDept}
      />
    </div>
  );
};

export default UserManagement;
